import { useTranslation } from 'react-i18next'
import { useMoney } from '@/lib/hooks/useMoney'
import { useCardQuote, type QuotableOption } from '@/lib/hooks/useCardQuote'
import { useYearlyPriceParts } from './useYearlyPriceParts'
import { cn } from '@/lib/utils'

export interface YearlyPriceProps {
  /** The yearly option (its id, installments and methods are what the quote is asked for). */
  option: QuotableOption
  /** What it charges up front: the option's price, plus the add-ons in `addOnIds` when there are any. */
  amount: number
  currency?: string
  /** Add-ons bought with the plan, quoted together (F-37). */
  addOnIds?: string[]
  /** The checkout's saving against the monthly option, as a green tag. */
  saving?: number | null
  /** One quiet line (a card's "Total"), instead of the big price. */
  compact?: boolean
  className?: string
}

/**
 * A yearly price as the plan's checkout shows it (David, 2026-10-30): on the
 * card, "12× de R$ X" from the server's quote (interest included when the
 * buyer pays it), then the up-front amount by PIX or boleto. An option that
 * does not split shows the year's price. Never computes a fee here.
 */
export function YearlyPrice({ option, amount, currency, addOnIds, saving = null, compact = false, className }: YearlyPriceProps) {
  const { t } = useTranslation(['checkout', 'common'])
  const formatMoney = useMoney()
  const quote = useCardQuote(option, addOnIds)
  const parts = useYearlyPriceParts({ option, amount, currency, addOnIds, saving })
  const money = (value: number) => formatMoney(value, currency)
  const card = quote ? t('checkout:annualPlan.installments', { count: quote.installments, amount: money(quote.each) }) : null

  if (compact) {
    return (
      /* Two lines reserved, so the buttons below line up whether the total splits on the card or not. */
      <p className={cn('min-h-[2.5rem] text-sm leading-5 text-gray-600', className)} data-testid="space-offer-total">
        {card
          ? t('common:access.offer.totalYearlyCard', { card, amount: money(amount) })
          : t('common:access.offer.totalLine', { total: money(amount), period: t('checkout:perYear') })}
      </p>
    )
  }

  return (
    <div className={className} data-testid="yearly-price">
      {parts.main}
      {parts.secondary}
      {parts.tag}
    </div>
  )
}
